import { s } from '@/theme/tokens';
import { useMediaQuery, useTheme } from '@/ui/primitives/hooks';

export type StackedMetricsStatusChartPanelLayout = {
  height: number;
  chartHeight: number;
  headerHeight: number;
  headerToChartGap: number;
  panelPadding: 'md';
  panelContentGap: 'md';
  headerTitlePaddingLeft: string;
  headerControlsPaddingRight: string;
  yAxisWidth: number;
  yAxisAutoWidth: boolean;
  yAxisAutoWidthPadding: number;
  axisStrokeWidth: number;
  barCornerRadius: number;
  yAxisLabelOffset: number;
  hideXAxisTicks: boolean;
  xAxisLabelOffset: number;
  chartMargins: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
};

export type StackedMetricsMiniPanelsLayout = {
  height: number;
  headerHeight: number;
  chartHeight: number;
  columnGap: string;
  rowGap: string;
  panelPaddingTop: string;
  panelPaddingBottom: string;
  headerPaddingX: string;
  headerPaddingTop: string;
  headerPaddingBottom: string;
  contentPaddingX: string;
  contentPaddingTop: string;
  contentPaddingBottom: string;
  chartWrapperPaddingX: string;
  chartWrapperPaddingTop: string;
  chartWrapperPaddingBottom: string;
  yAxisWidth: number;
  axisStrokeWidth: number;
  yDomainPaddingTopRatio: number;
  yDomainPaddingBottomRatio: number;
  chartMargins: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
};

export type StackedMetricsSectionLayout = {
  gap: string;
  rowGap: string;
  toolbarGap: string;
  bottomGap: string;
};

export type StackedMetricsStatusChartSectionLayout = {
  isNarrowChartLayout: boolean;
  isTwoColumnChartLayout: boolean;
  piePanelHeight: number;
  pieChartVisualHeight: number;
  statusChartVisualHeight: number;
  statusPanel: StackedMetricsStatusChartPanelLayout;
  miniPanels: StackedMetricsMiniPanelsLayout;
  sectionLayout: StackedMetricsSectionLayout;
};

export const useStackedMetricsStatusChartSectionLayout =
  (): StackedMetricsStatusChartSectionLayout => {
    const theme = useTheme();
    const isNarrowChartLayout = useMediaQuery(theme.breakpoints.down('lg'));
    const isTwoColumnChartLayout = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
    const metricsColumnHeight = 300;
    const sharedTwoColumnPanelHeight = 320;
    const miniPanelsRowGapPx = 16;
    const stackedSectionBaseRowHeight = (metricsColumnHeight - miniPanelsRowGapPx * 2) / 3;
    const piePanelHeight = isTwoColumnChartLayout
      ? sharedTwoColumnPanelHeight
      : isNarrowChartLayout
        ? 248
        : metricsColumnHeight;

    const statusPanel: StackedMetricsStatusChartPanelLayout = {
      height: isTwoColumnChartLayout
        ? sharedTwoColumnPanelHeight
        : isNarrowChartLayout
          ? 288
          : metricsColumnHeight,
      chartHeight: isTwoColumnChartLayout
        ? 268
        : isNarrowChartLayout
          ? 244
          : metricsColumnHeight - 42,
      headerHeight: 28,
      headerToChartGap: 24,
      panelPadding: 'md',
      panelContentGap: 'md',
      headerTitlePaddingLeft: s('md'),
      headerControlsPaddingRight: s('md'),
      yAxisWidth: 40,
      yAxisAutoWidth: true,
      yAxisAutoWidthPadding: 6,
      axisStrokeWidth: 0.75,
      barCornerRadius: 6,
      yAxisLabelOffset: -2,
      hideXAxisTicks: false,
      xAxisLabelOffset: 3,
      chartMargins: {
        left: -10,
        right: 16,
        top: 0,
        bottom: isNarrowChartLayout ? 14 : 18,
      },
    };

    const miniPanels: StackedMetricsMiniPanelsLayout = {
      height: stackedSectionBaseRowHeight,
      headerHeight: 28,
      chartHeight: 46,
      columnGap: s('lg'),
      rowGap: s('xl'),
      panelPaddingTop: s('sm'),
      panelPaddingBottom: s('none'),
      headerPaddingX: s('lg'),
      headerPaddingTop: s('none'),
      headerPaddingBottom: s('none'),
      contentPaddingX: s('md'),
      contentPaddingTop: s('sm'),
      contentPaddingBottom: s('none'),
      chartWrapperPaddingX: s('none'),
      chartWrapperPaddingTop: s('none'),
      chartWrapperPaddingBottom: s('none'),
      yAxisWidth: 0,
      axisStrokeWidth: 0.75,
      yDomainPaddingTopRatio: 0.08,
      yDomainPaddingBottomRatio: 0,
      chartMargins: {
        left: 6,
        right: 6,
        top: 2,
        bottom: -8,
      },
    };

    const sectionLayout: StackedMetricsSectionLayout = {
      gap: s('xl'),
      rowGap: s('xl'),
      toolbarGap: s('2xl'),
      bottomGap: s('none'),
    };

    const pieChartVisualHeight = isNarrowChartLayout
      ? piePanelHeight - miniPanels.headerHeight - (isTwoColumnChartLayout ? 78 : 52)
      : piePanelHeight - miniPanels.headerHeight - 72;
    const statusChartVisualHeight = statusPanel.chartHeight + 12;

    return {
      isNarrowChartLayout,
      isTwoColumnChartLayout,
      piePanelHeight,
      pieChartVisualHeight,
      statusChartVisualHeight,
      statusPanel,
      miniPanels,
      sectionLayout,
    };
  };
